import { CURRENCIES, CurrencyIso } from "types/currency"

export function getCurrencyCode() {
    return (localStorage.getItem('default_currency') as string) || 'SGD'
}

export function getCurrency(currencyCode?: string) {
    const currency_iso = currencyCode || getCurrencyCode()
    const currency = CURRENCIES.find(c => c.iso == currency_iso)
    return currency || {
        iso: CurrencyIso.SGD,
        symbol: '$',
    }
}

export function getCurrencySymbol(currencyCode?: string) {
    return getCurrency().symbol
}

export function toCurrency(
    val: number,
    currency = getCurrencyCode(),
    locale?: string
) {
    const df = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    })
    const parts = df.formatToParts(val)
    const amount: string[] = []
    parts.forEach((part) => {
        if (part.type != 'literal') amount.push(`${part.value}`)
    })
    return `${getCurrencySymbol(currency)} ${amount.slice(1).join('')}`
}

export function toYmd(val: Date, locale?: string) {
    const df = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        day: '2-digit',
        month: '2-digit',
    })
    const parts = df.formatToParts(val)
    let ymd: string = '{year}-{month}-{day}'
    parts.forEach((part) => {
        ymd = ymd.split(`{${part.type}}`).join(part.value)
    })
    return ymd
}

export function getFullTimeZone(tz: string, locale?: string) {
    const df = new Intl.DateTimeFormat(locale, {
        timeZoneName: 'long',
        timeZone: tz,
    })
    const results = df.formatToParts(new Date()) as unknown as Record<
        string,
        string
    >[]

    return results.filter(({ type }) => type === 'timeZoneName')[0].value
}

export function strToDate(d: Date, ts: string): Date {
    if (!ts) {
        return d
    }
    const [h, m] = ts.split(':') as unknown[] as number[]
    if (!h || !m) {
        return d
    }
    d.setHours(h)
    d.setMinutes(m)
    return d
}

export function addDurationToStr(date: Date, time: string, duration: number): string {
    if (!date || !time || !duration) {
        return ""
    }
    const split = time.split(":")
    date.setHours(split[0] as unknown as number)
    date.setMinutes(split[1] as unknown as number)
    date.setSeconds((split?.[2] || 0) as unknown as number)
    return toHHMM(new Date(date.getTime() + (duration * 60000)))
}

export function addDurationToStrDate(date: Date, time: string, duration: number): Date {
    if (!date || !time || !duration) {
        return new Date()
    }
    const split = time.split(":")
    date.setHours(split[0] as unknown as number)
    date.setMinutes(split[1] as unknown as number)
    date.setSeconds((split?.[2] || 0) as unknown as number)
    return new Date(date.getTime() + (duration * 60000))
}

export function toFullDate(val: Date) {
    return val.toLocaleDateString(undefined, { dateStyle: 'full' })
}

export function toShortFullDate(val: string) {
    const date = new Date(val)?.toLocaleString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    })

    return date
}

export function toFullTime(val: Date) {
    return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        hour12: false,
        minute: '2-digit',
        second: '2-digit',
    }).format(val)
}

export function toFullDateTime(val: Date) {
    const date = val?.toLocaleString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    })
    const time = val
        ?.toLocaleString(undefined, {
            hour12: true,
            minute: '2-digit',
            hour: 'numeric',
        })
        .toLocaleLowerCase()
        .split(' ')
        .join('')

    return `${date}, ${time}`
}

/**
 * Converts date into UTC format
 * 
 * @param val Date
 * @returns UTC date format
 */
export function toUTCDate(val: Date): Date {
    return new Date(val.toUTCString().substring(0, 25))
}

/**
 * Convert 12 hours time string to 24 hours time string
 * 
 * @param val 12 hours time format
 * @param has_no_space whether there is a space between the minutes and the meridiem
 * @param add_leading_zero whether to add a leading zero for hour and minute values
 * @returns 24 hours time format
 */
export function toHHMM24(val: string, has_no_space?: boolean, add_leading_zero?: boolean): string {
    let time = val.split(':')
    let hours = add_leading_zero && time[0].localeCompare("00") != 0 && parseInt(time[0]) < 10 ? '0' + time[0] : time[0]
    let minutes
    if (has_no_space) {
        const min = (time[1].match(/\d+/g) || ['00']).join('')
        minutes = add_leading_zero && min.localeCompare("00") != 0 && parseInt(min) < 10 ? '0' + min : min
    } else {
        minutes = time[1]
    }

    if (hours == '12') {
        hours = '00'
    }
    if (val.includes('AM') || val.includes('am')) {
        return `${hours}:${minutes}:00`
    } else {
        return `${parseInt(hours) + 12}:${minutes}:00`
    }
}

/**
 * Convert 12/24 hours time string to 12 hours time string with meridiem
 * 
 * @param val 12/24 hours time string
 * @returns 12 hours time string with meridiem
 */
export function timetoHHMM(val: string): string {
    let time = val.split(':')
    let hours = parseInt(time[0])
    let minutes = time[1]
    let meridiem = ''

    if (val.indexOf('AM') > -1) {
        meridiem = 'am'
    }
    else if (val.indexOf('PM') > -1) {
        meridiem = 'pm'
    }
    else {
        meridiem = hours < 12 ? 'am' : 'pm'
        if (hours > 12) {
            hours = hours - 12
        }
    }

    return `${hours}:${minutes}${meridiem}`
}

/**
 * Convert date format into 12 hours time string
 * 
 * @param val date
 * @param locale International DateTimeFormat
 * @param hide_seconds hides seconds
 * @param remove_space removes the space between the hhmm and the meridiem
 * @returns 12 hours time string format with meridiem
 */
export function toHHMM12(val: Date, locale?: string, hide_seconds?: boolean, remove_space?: boolean): string {
    if (!val || isNaN(val.getTime())) {
        return new Date().toDateString()
    }
    const df = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    })
    const parts = df.formatToParts(val)
    let hhmm: string = `{hour}:{minute}${hide_seconds ? '' : ':00'}`
    parts.forEach((part) => {
        if (['hour', 'minute'].indexOf(part.type) >= 0) {
            hhmm = hhmm.split(`{${part.type}}`).join(part.value)
        }
    })
    let meridiem = val.getHours() < 12 ? 'AM' : 'PM'
    return `${hhmm}${remove_space ? '' : ' '}${meridiem}`
}

/**
 * Convert date format to 24 hours time string
 * 
 * @param val date
 * @param locale International DateTimeFormat
 * @returns 24 hours time string
 */
export function toHHMM(val: Date, locale?: string): string {
    if (!val || isNaN(val.getTime())) {
        return new Date().toDateString()
    }
    const df = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
    let parts = df.formatToParts(val)
    if (parts[0].value == "24") { // chrome sends 24 instead of 00
        parts[0].value = "00"
    }
    let hhmm: string = '{hour}:{minute}:00'
    parts.forEach((part) => {
        if (['hour', 'minute'].indexOf(part.type) >= 0) {
            hhmm = hhmm.split(`{${part.type}}`).join(part.value)
        }
    })
    return hhmm
}

/**
 * Convert date format into 12 hours time string
 * 
 * @param val date
 * @param locale International DateTimeFormat
 * @returns 12 hours time string
 */
export function toHourMinute(val: Date, locale?: string) {
    console.log(val, locale, "slottttttt");

    const df = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
    })
    const parts = df.formatToParts(val)
    let hhmm: string = '{hour}:{minute}'
    let ampm = ''
    let hour = 0
    parts.forEach((part) => {
        if (part.type == 'hour') {
            hour = Number(part.value)
            if (hour >= 12) {
                ampm = 'PM'
                if (hour > 12) {
                    hour = hour - 12
                }
            } else {
                ampm = 'AM'
            }

            hhmm = hhmm.split(`{${part.type}}`).join(`${hour}`)
        }
        if (part.type == 'minute') {
            hhmm = hhmm.split(`{${part.type}}`).join(part.value)
        }
    })
    return `${hhmm}${ampm}`
}

/**
 * Convert date format into hour only time string
 *  
 * @param val date
 * @param locale International DateTimeFormat
 * @returns hour only time string
 */
export function toHour(val: Date, locale?: string) {
    let ampm = 'am'
    let hour = val.getHours()
    if (hour >= 12) {
        ampm = 'pm'
        if (hour > 12) {
            hour -= 12
        }
    }
    if (hour === 0) hour = 12

    return `${hour} ${ampm}`
}

export function convertMinsToHrsMins(mins: number, no_mins_verbiage?: boolean) {
    
    if (mins == 0) {
        return '0 hour'
    }

    let h = Math.floor(mins / 60)
    let m = mins % 60
    let hs = h == 0 ? '' : `${h} hour${h > 1 ? 's ' : ' '}`
    let ms = m == 0 ? '' : m == 1 ? `${m} min ` : `${m} mins `

    if (no_mins_verbiage) {
        return `${hs} ${m > 0 ? m : 0}`
    }
    return `${hs}${ms}`
}

export function subtractHours(numOfHours: number, date: Date) {
    const dateCopy = new Date(date.getTime());
    dateCopy.setHours(dateCopy.getHours() - numOfHours);
    return dateCopy;
}

export function addHours(numOfHours: number, date: Date) {
    const dateCopy = new Date(date.getTime());
    dateCopy.setHours(dateCopy.getHours() + numOfHours);
    return dateCopy;
}

export function parseISOString(s: string): Date {
    var b = s.split(/\D+/);
    return new Date(Date.UTC
        (
            b?.[0] as unknown as number,
            --(b[1] as unknown as number),
            b?.[2] as unknown as number,
            b?.[3] as unknown as number,
            b?.[4] as unknown as number,
            b?.[5] as unknown as number,
            b?.[6] as unknown as number
        ));
}

/**
 * Calculates the closest Date matching a given Day from a given start Date
 *  
 * @param day 0 - Sunday, ..., 6 -Saturday
 * @param start the starting Date
 * @returns date that matches the day
 */
export function getClosestDateFromDay(day: number, start: Date) {
    if (start.getDay() == day) {
        return start
    } else {
        let new_date = new Date(start.getTime())
        const offset = new_date.getDay() > day ? day + 7 - new_date.getDay() : day - new_date.getDay()
        new_date.setDate(new_date.getDate() + offset)
        return new_date
    }
}

export function isSameDay(date_a: Date, date_b: Date): boolean {
    return (
        date_a.getFullYear() == date_b.getFullYear()
        && date_a.getMonth() == date_b.getMonth()
        && date_a.getDate() == date_b.getDate()
    )
}

export function hasTimeOverlap(start_a: number[], end_a: number[], start_b: number[], end_b: number[]): boolean {
    return (compareTime(start_a, end_b) <= 0 && compareTime(end_a, start_b) >= 0)
}

export function compareTime(time_a: number[], time_b: number[]): number {
    if (time_a?.[0] == time_b?.[0]) {
        if (time_a?.[1] == time_b?.[1]) {
            return time_a?.[2] - time_b?.[2]
        } else {
            return time_a?.[1] - time_b?.[1]
        }
    } else {
        return time_a?.[0] - time_b?.[0]
    }
}

export function getFirstAndLastDay(date: Date) {
    const y = date.getFullYear()
    const m = date.getMonth();
    const first_day = new Date(y, m, 1, 0, 0, 0, 0);
    const last_day = new Date(y, m + 1, 0, 0, 0, 0);

    return [first_day, last_day]
}

export function isPreviousDate(a: Date, b: Date): boolean {
    a.setHours(0, 0, 0,0)
    b.setHours(0,0,0,0)
    return a.getTime() < b.getTime()
}